import { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppPlatform } from "app/types";
import { AppDispatch, RootState } from "app/store";
import { NativePackagesStatus } from "./types";
import {
  setNativePackages,
  setNativePackagesFailed,
  setNativePackagesLoading,
  setNativePackagesNotSupported,
} from "./subscriptionSlice";

export type PlanCategory = "standard" | "premium";
export type BillingPeriod = "monthly" | "annual";

// デフォルト価格（フォールバック用）
const DEFAULT_PRICES: Record<
  PlanCategory,
  Record<BillingPeriod, { price: number; priceString: string }>
> = {
  standard: {
    monthly: { price: 500, priceString: "¥500" },
    annual: { price: 5000, priceString: "¥5,000" },
  },
  premium: {
    monthly: { price: 1000, priceString: "¥1,000" },
    annual: { price: 10000, priceString: "¥10,000" },
  },
};

export const useNativePackages = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { packages, status } = useSelector((state: RootState) => state.subscription.nativePackages);
  const platform = (window.kidsAppPlatform as AppPlatform) || AppPlatform.Web;
  const isNativeApp = platform === AppPlatform.iOS || platform === AppPlatform.Android;

  const requestPackages = useCallback(() => {
    if (!isNativeApp || !window.ReactNativeWebView) return;

    dispatch(setNativePackagesLoading());
    window.ReactNativeWebView.postMessage(JSON.stringify({ command: "getPackages" }));
  }, [dispatch, isNativeApp]);

  useEffect(() => {
    if (!isNativeApp || !window.ReactNativeWebView) return;

    const handleMessage = (message: MessageEvent) => {
      try {
        const { command, data } = JSON.parse(typeof message.data === "string" ? message.data : "");
        if (command === "getPackages") {
          if (data.result === "success") {
            dispatch(setNativePackages(data.packages));
          } else if (data.result === "not_supported") {
            dispatch(setNativePackagesNotSupported());
          } else {
            dispatch(setNativePackagesFailed());
          }
        }
      } catch {
        // ignore non-JSON messages
      }
    };

    if (platform === AppPlatform.iOS) {
      window.addEventListener("message", handleMessage);
    } else {
      document.addEventListener("message", handleMessage as EventListener);
    }

    return () => {
      if (platform === AppPlatform.iOS) {
        window.removeEventListener("message", handleMessage);
      } else {
        document.removeEventListener("message", handleMessage as EventListener);
      }
    };
  }, [dispatch, isNativeApp, platform]);

  // パッケージ検索ヘルパー
  const findPackage = useCallback(
    (planCategory: PlanCategory, billingPeriod: BillingPeriod) => {
      return packages.find((p) => {
        const id = p.identifier.toLowerCase();
        const matchesPlan =
          planCategory === "standard" ? id.includes("standard") : id.includes("premium");
        const matchesPeriod =
          billingPeriod === "monthly"
            ? id.includes("monthly")
            : id.includes("annual") || id.includes("yearly");
        return matchesPlan && matchesPeriod;
      });
    },
    [packages],
  );

  // 価格取得ヘルパー
  const getPrice = useCallback(
    (
      planCategory: PlanCategory,
      billingPeriod: BillingPeriod,
    ): { price: number; priceString: string } => {
      const pkg = findPackage(planCategory, billingPeriod);
      if (pkg) {
        return { price: pkg.price, priceString: pkg.priceString };
      }
      return DEFAULT_PRICES[planCategory][billingPeriod];
    },
    [findPackage],
  );

  // identifier取得ヘルパー（RevenueCat購入時に使用）
  const getIdentifier = useCallback(
    (planCategory: PlanCategory, billingPeriod: BillingPeriod): string | undefined => {
      return findPackage(planCategory, billingPeriod)?.identifier;
    },
    [findPackage],
  );

  return {
    packages,
    status,
    isLoading: status === NativePackagesStatus.LOADING,
    isNativeApp,
    requestPackages,
    getPrice,
    getIdentifier,
  };
};
